import React from 'react';
import { reduxForm, Field, InjectedFormProps, stopSubmit } from 'redux-form';
import { DispatchProp } from 'react-redux';
import { TextField, Button, MfaCountdownTimer } from 'components/index';
import { FormTile } from 'components/FormTile';
import { validate } from './validate';
import { getReduxFormError } from 'utils/forms/getReduxFormError';
import { EntityWithMeta } from 'api/types';
import { useMfaTimer } from 'hooks/useMfaTimer';

export interface MfaCodeFormData {
  code: string;
}

interface OwnProps extends DispatchProp {
  isFormProcessed: boolean;
  onResendCode: () => Promise<EntityWithMeta<unknown>>;
  onBackToLogin: () => void;
  onSubmit: (data: MfaCodeFormData) => void;
  isTimerActive?: boolean;
}

type Props = InjectedFormProps<MfaCodeFormData, OwnProps> & OwnProps;

class MfaCodeFormComponent extends React.PureComponent<Props> {
  handleResendCode = async () => {
    this.props.reset();
    this.props.clearSubmit();
    const response = await this.props.onResendCode();
    if (response?.code !== 'success') {
      this.props.dispatch(
        stopSubmit(this.props.form, getReduxFormError(response)),
      );
    }
  };

  render() {
    const {
      handleSubmit,
      error,
      submitFailed,
      isFormProcessed,
      onBackToLogin,
      isTimerActive,
    } = this.props;

    return (
      <form
        onSubmit={handleSubmit}
        className="form-tile-with-image "
        data-testid="mfa-verification-screen"
      >
        <FormTile isLoaderShow={isFormProcessed}>
          <FormTile.Header>
            <div className="form-description-logo">
              <img
                className="form-description-logo__img"
                src="/assets/images/logo/description-logo@2x.png"
                alt="Anova logo"
              />
            </div>
          </FormTile.Header>
          <FormTile.Body>
            <h2 className="mt-25">Confirmation Code</h2>
            <div
              className="mt-1 form-fields__description"
              data-testid="mfa-instruction-message"
            >
              To ensure your account security, Multi-Factor Authentication (MFA)
              via email verification is required. We have sent a unique
              verification code to your registered email address.
            </div>

            <Field
              label="Code:"
              name="code"
              component={TextField}
              type="text"
              maxLength={6}
              isBackgroundErrorDisplay={false}
              fieldClassName="mt-14"
              props={{ testId: 'mfa-verification-code-input' }}
            />
            <div className="mt-10 sign-up-form__terms">
              {isTimerActive ? (
                <MfaCountdownTimer />
              ) : (
                <Button
                  onClick={this.handleResendCode}
                  className="_simple-red"
                  type="button"
                  dataTestId="resend-verification-code"
                >
                  Resend Verification Code
                </Button>
              )}
            </div>
          </FormTile.Body>
          <FormTile.Footer>
            {error && submitFailed && (
              <div
                className="form-tile-footer__error"
                data-testid="mfa-error-message"
              >
                {error}
              </div>
            )}
            <div className="form-tile-footer__panel">
              <Button
                onClick={onBackToLogin}
                className="_simple-red"
                type="button"
                dataTestId="back-to-login-form"
              >
                Back to Login Form
              </Button>
              <Button
                className="_red"
                type="submit"
                dataTestId="confirm-verification-code"
              >
                Submit
              </Button>
            </div>
          </FormTile.Footer>
        </FormTile>
      </form>
    );
  }
}

// Wrapper component that uses the hook and passes timer state to the form component
const MfaCodeFormWithTimer: React.FC<Props> = (props) => {
  const { isTimerActive } = useMfaTimer();

  return <MfaCodeFormComponent {...props} isTimerActive={isTimerActive} />;
};

export const MfaCodeForm = reduxForm<MfaCodeFormData, OwnProps>({
  form: 'mfaCodeForm',
  validate,
})(MfaCodeFormWithTimer);
